<template>
  <div class="company-information">
    <h3>Company Information</h3>
    <div class="input-container">
      <div class="double-column">
        <TextBox label="Company Name" class="double" v-model="company.NAME" :invalid="!company.NAME && showValidationErrors" />
        <TextBox label="Phone" format="phone" v-model="mainPhone" :invalid="!mainPhone && showValidationErrors" />
        <TextBox label="CCB / LCB" v-model="company.CCB" />
        <TextBox label="Fax" format="phone" v-model="faxPhone" />
        <TextBox label="Toll Free" format="phone" v-model="tollFreePhone" />
        <TextBox label="Website" class="double" v-model="company.WEBADDRESS" :showExtLink="!!company.WEBADDRESS" @navigateToLink="navigateToWebsite()" />
      </div>
      <div class="double-column">
        <AddressSearch
          label="Address Line 1"
          class="double"
          v-model="company.address.LINE1"
          :options="lookupResults"
          :showExtLink="true"
          @navigateToLink="navigateToAddress()"
          @autofillAddress="autofillAddress"
          @clearLookup="lookupResults.length = 0" />
        <TextBox label="Line 2" v-model="company.address.LINE2" />
        <AddressSearch label="City" v-model="company.address.CITY" :options="lookupResults" @autofillAddress="autofillAddress" @clearLookup="lookupResults.length = 0" />
        <Dropdown label="State" v-model="company.address.STATE" :options="stateOptions" :canBeEmpty="true" />
        <AddressSearch label="Zip" v-model="company.address.POSTALCODE" :options="lookupResults" @autofillAddress="autofillAddress" @clearLookup="lookupResults.length = 0" />
        <Dropdown label="Country" class="double" v-model="company.address.COUNTRYNAME" :options="countryOptions" :canBeEmpty="true" />
      </div>
    </div>
  </div>
</template>

<script>
import TextBox from './inputs/TextBox.vue';
import Dropdown from './inputs/Dropdown.vue';
import { phoneTypes } from '../composables/usePhoneTypes.js';
import useStatesAndCountries from '../composables/useStatesAndCountries.js';
import useAddressLookup from '../composables/useAddressLookup.js';
import AddressSearch from './inputs/AddressSearch.vue';

export default {
  name: 'CompanyInformation',
  components: {
    TextBox,
    Dropdown,
    AddressSearch,
  },

  props: {
    company: {
      type: Object,
      default: null,
    },
    showValidationErrors: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { stateAbbreviations, countries } = useStatesAndCountries();
    const { addressLookup, lookupResults } = useAddressLookup();

    return {
      stateAbbreviations,
      countries,
      addressLookup,
      lookupResults,
    };
  },

  created() {
    this.initializeCompany();
  },

  watch: {
    company: {
      handler() {
        this.initializeCompany();
      },
      immediate: true,
    },
    'company.address.LINE1'() {
      this.updateAddressLookup();
    },
    'company.address.CITY'() {
      this.updateAddressLookup();
    },
    'company.address.STATE'() {
      this.updateAddressLookup();
    },
    'company.address.POSTALCODE'() {
      this.updateAddressLookup();
    },
  },

  computed: {
    stateOptions() {
      return this.stateAbbreviations;
    },

    countryOptions() {
      return this.countries;
    },

    mainPhone: {
      get() {
        const phone = this.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.main);
        return phone?.NUMBERDISPLAY || '';
      },
      set(value) {
        const phone = this.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.main);
        if (phone) {
          phone.NUMBERDISPLAY = value;
        }
      },
    },

    faxPhone: {
      get() {
        const phone = this.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.fax);
        return phone?.NUMBERDISPLAY || '';
      },
      set(value) {
        const phone = this.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.fax);
        if (phone) {
          phone.NUMBERDISPLAY = value;
        }
      },
    },

    tollFreePhone: {
      get() {
        const phone = this.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.tollFree);
        return phone?.NUMBERDISPLAY || '';
      },
      set(value) {
        const phone = this.company?.phones?.find((phone) => phone.TYPEID === phoneTypes.company.tollFree);
        if (phone) {
          phone.NUMBERDISPLAY = value;
        }
      },
    },
  },
  methods: {
    initializeCompany() {
      if (this.company) {
        // Ensure address object exists
        if (!this.company.address) {
          this.company.address = {};
        }

        // Ensure phones array exists
        if (!this.company.phones) {
          this.company.phones = [];
        }

        // Ensure all required phone types exist
        const requiredPhoneTypes = [phoneTypes.company.main, phoneTypes.company.fax, phoneTypes.company.tollFree];

        requiredPhoneTypes.forEach((typeId) => {
          const existingPhone = this.company.phones.find((phone) => phone.TYPEID === typeId);
          if (!existingPhone) {
            this.company.phones.push({
              NUMBERDISPLAY: '',
              TYPEID: typeId,
              __typename: 'TBL_PHONE',
            });
          }
        });
      }
    },
    autofillAddress(option) {
      if (option.address) this.company.address.LINE1 = option.address;
      if (option.city) this.company.address.CITY = option.city;
      if (option.state) this.company.address.STATE = option.state;
      if (option.zip) this.company.address.POSTALCODE = option.zip;
    },
    updateAddressLookup() {
      if (this.company?.address?.LINE1 && this.company?.address?.CITY && this.company?.address?.STATE && this.company?.address?.POSTALCODE) return;
      this.addressLookup(this.company?.address?.LINE1, this.company?.address?.CITY, this.company?.address?.STATE, this.company?.address?.POSTALCODE);
    },
    navigateToWebsite() {
      window.open('https://' + this.company.WEBADDRESS, '_blank');
    },
    navigateToAddress() {
      window.open(
        `https://www.google.com/maps/search/${encodeURIComponent(this.company.address.LINE1)} ${encodeURIComponent(this.company.address.LINE2)} ${encodeURIComponent(this.company.address.CITY)} ${encodeURIComponent(
          this.company.address.STATE
        )} ${encodeURIComponent(this.company.address.POSTALCODE)}`,
        '_blank'
      );
    },
  },
};
</script>

<style scoped lang="scss">
.company-information {
  flex: 5 0 45%;
  max-width: 80rem;

  .input-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1.5rem;

    .double-column {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1rem;
      flex: 1 0 48%;
      min-width: 20rem;

      & > * {
        flex: 1 0 48%;
      }

      .double {
        flex: 1 0 100%;
      }
    }
  }
}
</style>
